/**
 * useSlideTransform.ts — drives the AI slide redesign for the current selection.
 *
 * Reads the selected slides out of the open deck, streams them through the
 * transform endpoint, and writes each redesigned slide back as its result
 * arrives, so a multi-slide run updates the deck progressively instead of
 * waiting for the whole stream.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { config } from "../../../../../shared/config/environment";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import {
  streamSlideTransform,
  type SlideTransformResultEvent,
  type SlideTransformStatusEvent,
} from "../services/slideTransformApi";
import { buildTransformSource, type TransformSource } from "../services/slideTransformSource";
import {
  getSelectedSlides,
  NoSlideSelectedError,
  type SelectedSlide,
} from "../services/slideSelection";
import {
  buildTransformedSlidePackage,
  insertTransformedSlideAfter,
} from "../services/slideTransformWriteback";
import {
  getErrorMessage,
  logSlideTransformError,
} from "../services/slideTransformTelemetry";

export type SlideTransformPhase =
  | "idle"
  | "reading"
  | "transforming"
  | "done"
  | "cancelled"
  | "error";

export type SlideProgressStatus = "waiting" | "designing" | "applying" | "done" | "failed";

export interface SlideProgress {
  /** 0-based presentation position. */
  readonly index: number;
  readonly status: SlideProgressStatus;
  readonly detail?: string;
}

export interface SlideTransformState {
  readonly phase: SlideTransformPhase;
  readonly slides: readonly SlideProgress[];
  /** Set only when `phase` is `"error"` — a run-level failure. */
  readonly error?: string;
}

const IDLE_STATE: SlideTransformState = { phase: "idle", slides: [] };

const READING_DECK_MESSAGE = "Couldn't read the presentation.";
const TRANSFORM_FAILED_MESSAGE = "Slide redesign failed.";
const APPLY_FAILED_MESSAGE = "Couldn't add the redesigned slide.";
const INVALID_RESULT_MESSAGE = "The redesign produced an unusable slide, so it wasn't applied.";
const NO_RESULTS_MESSAGE =
  "The server didn't return any redesigned slides. Check that the Deckorate API is configured and reachable.";

/** Waiting-state rows so the UI lists every selected slide before work starts. */
function toInitialProgress(slides: readonly SelectedSlide[]): SlideProgress[] {
  return slides.map(({ index }) => ({ index, status: "waiting" as const }));
}

export interface UseSlideTransformResult {
  readonly state: SlideTransformState;
  /** Redesign the current slide selection. No-op while a run is in flight. */
  readonly transform: () => Promise<void>;
  /** Abort the in-flight run. Slides already applied stay applied. */
  readonly cancel: () => void;
  readonly reset: () => void;
}

export function useSlideTransform(): UseSlideTransformResult {
  const http = useDeckorateHttp();
  const [state, setState] = useState<SlideTransformState>(IDLE_STATE);

  // Guards a second run starting while one is in flight, matching the
  // double-click guard in useSpeakerNotes/useBrandApply.
  const isRunningRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Abandon the stream when the panel unmounts so the fetch and its reader
      // are released rather than writing into a deck the user has navigated away from.
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  /** Ignore updates that land after unmount — the run continues to settle, the UI doesn't. */
  const safeSetState = useCallback(
    (update: (previous: SlideTransformState) => SlideTransformState) => {
      if (isMountedRef.current) setState(update);
    },
    [],
  );

  const patchSlide = useCallback(
    (index: number, status: SlideProgressStatus, detail?: string) => {
      safeSetState((previous) => ({
        ...previous,
        slides: previous.slides.map((slide) =>
          slide.index === index ? { index, status, ...(detail ? { detail } : {}) } : slide,
        ),
      }));
    },
    [safeSetState],
  );

  /**
   * Build the single-slide package for a result and swap it into the deck.
   * Per-slide failures are surfaced on that slide's row and never abort the run.
   */
  const applyResult = useCallback(
    async (
      result: SlideTransformResultEvent,
      source: TransformSource,
      slideIdByIndex: ReadonlyMap<number, string>,
    ): Promise<void> => {
      const partPath = source.partPathByIndex.get(result.slide_index);
      const slideId = slideIdByIndex.get(result.slide_index);
      if (!partPath || !slideId) {
        patchSlide(result.slide_index, "failed", APPLY_FAILED_MESSAGE);
        return;
      }

      // `valid: false` means the backend fell back to the slide's own shapes —
      // re-applying them would be a no-op write, so report it instead.
      if (!result.valid) {
        patchSlide(result.slide_index, "failed", result.error ?? INVALID_RESULT_MESSAGE);
        return;
      }

      patchSlide(result.slide_index, "applying");
      try {
        const { base64 } = buildTransformedSlidePackage(
          source.files,
          partPath,
          result.slide_index,
          result.shapeTreeXml,
        );
        await insertTransformedSlideAfter(slideId, base64);
        patchSlide(result.slide_index, "done");
      } catch (error) {
        const message = getErrorMessage(error, APPLY_FAILED_MESSAGE);
        logSlideTransformError("slide-transform-apply", message);
        patchSlide(result.slide_index, "failed", message);
      }
    },
    [patchSlide],
  );

  const transform = useCallback(async (): Promise<void> => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;
    safeSetState(() => ({ phase: "reading", slides: [] }));

    // Applies are chained so two PowerPoint.run calls never overlap.
    let applyChain: Promise<void> = Promise.resolve();

    try {
      const selected = await getSelectedSlides();
      const slideIdByIndex = new Map(selected.map(({ index, id }) => [index, id] as const));
      safeSetState(() => ({ phase: "reading", slides: toInitialProgress(selected) }));

      const source = await buildTransformSource(selected);
      if (controller.signal.aborted) {
        // Must land on a terminal phase: "reading" keeps the primary action
        // disabled, so returning without this leaves the panel stuck.
        safeSetState((previous) => ({ ...previous, phase: "cancelled" }));
        return;
      }

      safeSetState((previous) => ({ ...previous, phase: "transforming" }));

      await streamSlideTransform(
        source.request,
        {
          onStatus: (event: SlideTransformStatusEvent) => {
            // Only reflect in-progress stages; the terminal one is owned by the apply step.
            if (event.state !== "done") patchSlide(event.slide_index, "designing", event.stage);
          },
          onResult: (result) => {
            applyChain = applyChain.then(() => applyResult(result, source, slideIdByIndex));
          },
          onError: (event) => {
            logSlideTransformError("slide-transform-stream", event.error.message);
            patchSlide(event.slide_index, "failed", event.error.message);
          },
        },
        {
          baseUrl: config.deckorateApiBaseUrl,
          authorizedFetch: http.fetch,
          signal: controller.signal,
          onMalformedFrame: (event, error) =>
            logSlideTransformError(
              "slide-transform-frame",
              `Skipped malformed "${event}" frame: ${getErrorMessage(error, "unparseable payload")}`,
            ),
        },
      );

      await applyChain;

      if (controller.signal.aborted) {
        safeSetState((previous) => ({ ...previous, phase: "cancelled" }));
        return;
      }

      safeSetState((previous) => {
        const settled = previous.slides.filter(
          (slide) => slide.status === "done" || slide.status === "failed",
        );

        // The stream ended without a single slide reaching a terminal state, so
        // no `result` or `error` frame ever arrived. A 200 that is not actually
        // an event stream does this — most commonly the Deckorate base URL being
        // unset, which makes the request resolve against the add-in's own host
        // and return the SPA shell. Reporting "done" there would leave the user
        // looking at an unchanged deck with no explanation.
        if (settled.length === 0) {
          logSlideTransformError("slide-transform-empty", NO_RESULTS_MESSAGE);
          return { ...previous, phase: "error", error: NO_RESULTS_MESSAGE };
        }

        // Per-slide failures are already shown on their own rows, so the run only
        // reads as failed when nothing at all came through.
        const allFailed = settled.every((slide) => slide.status === "failed");
        return allFailed
          ? { ...previous, phase: "error", error: TRANSFORM_FAILED_MESSAGE }
          : { ...previous, phase: "done" };
      });
    } catch (error) {
      if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) {
        safeSetState((previous) => ({ ...previous, phase: "cancelled" }));
        return;
      }

      const fallback = error instanceof NoSlideSelectedError ? error.message : READING_DECK_MESSAGE;
      const message = getErrorMessage(error, fallback);
      logSlideTransformError("slide-transform-run", message);
      safeSetState((previous) => ({ ...previous, phase: "error", error: message }));
    } finally {
      isRunningRef.current = false;
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [applyResult, http, patchSlide, safeSetState]);

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    safeSetState(() => IDLE_STATE);
  }, [safeSetState]);

  return { state, transform, cancel, reset };
}
